import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCookieBite, FaShieldAlt, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';

// Lightweight cookie helpers (no extra dependencies)
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
};

const CONSENT_KEY = 'appozy_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Small delay so it doesn't flash on every hydration
    const timer = setTimeout(() => {
      if (!getCookie(CONSENT_KEY)) setVisible(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const accept = () => {
    setCookie(CONSENT_KEY, 'all', 365);
    setVisible(false);
  };

  const necessary = () => {
    setCookie(CONSENT_KEY, 'necessary', 365);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          className="fixed bottom-4 left-1/2 z-[99999] w-[calc(100%-2rem)] max-w-2xl"
          style={{ transform: 'translateX(-50%) translateY(0)', x: '-50%' }}
        >
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(15, 10, 30, 0.92)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(139, 92, 246, 0.25)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(139,92,246,0.1)',
            }}
          >
            {/* Top gradient accent */}
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: 'linear-gradient(90deg, #8b5cf6, #14b8a6, #8b5cf6)' }}
            />

            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}
                >
                  <FaCookieBite className="text-white text-base" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-white font-semibold text-sm">We use cookies</h3>
                    <button
                      onClick={necessary}
                      className="text-gray-500 hover:text-gray-300 transition-colors shrink-0"
                      aria-label="Dismiss"
                    >
                      <FaTimes className="text-xs" />
                    </button>
                  </div>

                  <p className="text-gray-400 text-xs leading-relaxed">
                    Appozy uses cookies to keep you logged in securely and improve your experience — no tracking or ad cookies. <Link to="/cookies" className="text-violet-400 hover:underline">Cookie Policy</Link>.{' '}
                    <button
                      onClick={() => setShowDetails((v) => !v)}
                      className="text-violet-400 hover:text-violet-300 underline underline-offset-2 transition-colors"
                    >
                      {showDetails ? 'Hide details' : 'Learn more'}
                    </button>
                  </p>

                  {/* Details panel */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 space-y-2">
                          {[
                            {
                              icon: '🔐',
                              label: 'Authentication',
                              desc: 'Secure HttpOnly session cookies — invisible to JavaScript, immune to XSS attacks.',
                              type: 'Always on',
                              color: 'text-emerald-400',
                            },
                            {
                              icon: '⚙️',
                              label: 'Preferences',
                              desc: 'Remembers your theme, timezone, and display settings across sessions.',
                              type: 'Always on',
                              color: 'text-emerald-400',
                            },
                            {
                              icon: '📊',
                              label: 'Analytics',
                              desc: 'Anonymous usage data to improve the product — no personal information shared.',
                              type: 'Optional',
                              color: 'text-amber-400',
                            },
                          ].map((item) => (
                            <div
                              key={item.label}
                              className="flex items-start gap-3 rounded-xl p-3"
                              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                            >
                              <span className="text-sm mt-0.5">{item.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-white text-xs font-medium">{item.label}</span>
                                  <span className={`text-[10px] font-semibold ${item.color}`}>{item.type}</span>
                                </div>
                                <p className="text-gray-500 text-[11px] leading-snug">{item.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="mt-2 text-[11px] text-gray-600">
                          Read our{' '}
                          <Link to="/privacy" className="text-violet-400 hover:underline" onClick={necessary}>
                            Privacy Policy
                          </Link>{' '}
                          for full details.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action buttons */}
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <button
                      onClick={accept}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95"
                      style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}
                    >
                      Accept All
                    </button>
                    <button
                      onClick={necessary}
                      className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-300 transition-all duration-200 hover:text-white active:scale-95"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      Necessary Only
                    </button>
                    <div className="flex items-center gap-1.5 ml-auto text-[10px] text-gray-600">
                      <FaShieldAlt className="text-emerald-500 text-[10px]" />
                      <span>No tracking or ads</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
